// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import L from 'leaflet';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import './feature/featureViewer';
import 'datatables';
import 'datatables-scroller';
import 'datatables-colreorder';
import 'datatables-colresize';
import 'datatables-colvis';
import 'datatables-naturalSort';

myw.DataTable = myw.MywClass.extend('DataTable', {
    //Maps the database field types with column types used in dataTables
    typeMap: {
        date: 'date',
        double: 'num',
        integer: 'num',
        numeric: 'num',
        default: 'natural'
    },
    /**
     * Builds a grid with the features supplied and attaches it to the DOM element with id = gridId
     * @class Uses the [DataTables]{@link http://www.datatables.net/} javascript library to show a list of myWorld objects in a tabular/grid view
     * @constructs
     * @param  {Control}        owner       The component which is the owner of self.
     * @param  {String}             gridId      Id of the div the grid will be attached tp
     * @param  {array<Feature>} features    features to be displayed in a grid using dataTables
     * @param  {function}           option.onFilterChange Callback for when the filter changes
     */
    initialize(owner, gridId, features, options) {
        this.app = owner.app;
        this.gridId = gridId;
        this.features = features;
        this.setOptions(options);

        const feature = features[0],
            fieldsOrder = feature.getFieldsOrder(),
            fieldsDD = feature.getFieldsDD();
        this.createColumnsAndHeader(fieldsOrder, fieldsDD);
        this.dataSet = this.createDataSet(fieldsOrder, fieldsDD);
        this.buildTable();
        this.app.on('currentFeature-changed', e => {
            this._scrollToFeature(e.feature);
        });
    },

    /**
     * Uses the fields to create a columns array for DataTables and an HTML table header object for the grid
     * @param  {string[]} fieldsOrder Array of fields organized in display order
     * @param  {object<fieldDD>}  fieldsDD    Field DD information keyed on field name
     */
    createColumnsAndHeader(fieldsOrder, fieldsDD) {
        let fieldDD;
        const columns = [];
        const gridTableHeaderRow = $('<tr></tr>');
        let fieldType;
        let fieldTypeFromRecord;
        let headerName;

        // Build the columns list and their corresponding grid header cells
        _.each(fieldsOrder, fieldName => {
            // get the field's dd
            fieldDD = fieldsDD[fieldName];

            if (fieldDD.type) {
                fieldTypeFromRecord = fieldDD.type.split('(')[0];
                fieldType = this.typeMap[fieldTypeFromRecord]
                    ? this.typeMap[fieldTypeFromRecord]
                    : this.typeMap['default'];
                if (fieldDD.unit) fieldType = this.typeMap['default'];
            } else fieldType = this.typeMap['default'];

            columns.push({ data: fieldName, type: fieldType });
            // If the field has a unit, add it to its header
            if (fieldDD) headerName = fieldDD.external_name;
            else headerName = '';

            gridTableHeaderRow.append(`<th>${headerName}</th>`);
        });
        // Prepend the column list with URN (as the hidden first column) and the Object name
        columns.unshift(
            { data: 'urn', type: this.typeMap['default'] },
            { data: 'myw_feature_title', type: 'natural' }
        );
        columns.push({ data: '-', type: this.typeMap['default'] }); // Dummy blank column to span the remaining available width

        gridTableHeaderRow.prepend(`<th>${this.msg('object')}</th>`);
        gridTableHeaderRow.prepend(`<th>${this.msg('object_urn')}</th>`);
        gridTableHeaderRow.append('<th>' + '' + '</th>'); // Dummy blank column to span the remaining available width

        this.columns = columns;
        this.tableHeader = $('<thead></thead>').append(gridTableHeaderRow);
        this.translate(this.tableHeader);
    },

    /**
     * Create a JSON array listing the features to be passed to the DataTables library
     * @param  {string[]}        fieldsOrder Order in which to display fields
     * @param  {object<fieldDD>} fieldsDD    Field DD information keyed on field name
     * @return {array<object>}               List of features properties
     */
    createDataSet(fieldsOrder, fieldsDD) {
        const dataSet = [];
        let obj;
        let fieldDD;
        let fieldName;

        const //ENH: have a different way to get fieldViewers
            featureViewer = new myw.FeatureViewer(this);

        // Add a new element for each feature in the list.
        _.each(this.features, feature => {
            obj = {};

            for (let i = 0; i < fieldsOrder.length; i++) {
                fieldName = fieldsOrder[i];
                fieldDD = fieldsDD[fieldName];

                if (fieldDD) {
                    const fieldViewer = featureViewer.getFieldViewer(feature, fieldDD, {
                        inListView: true
                    });
                    const baseType = fieldDD.type.split('(')[0],
                        hasMapping = this.typeMap[baseType];

                    if (hasMapping) {
                        //The field has a mapping so we need the content, otherwise sorting won't work
                        //Keep the 'num' and 'date' type fields as just text
                        obj[fieldName] = fieldViewer.$el.text();
                    } else {
                        //For string sorting we can pass a html string
                        //Datatables doesn't seem to support passing elements (or jquery elements)
                        //Using outerHTML causes any event handlers to be lost
                        obj[fieldName] = fieldViewer.el.outerHTML;
                    }
                }
            }
            //Add an extra property for URN (will be hidden in the grid)
            obj['urn'] = feature.getUrn();
            obj['myw_feature_title'] = _.escape(feature.getTitle());
            obj['-'] = ''; // Dummy blank column data to span the remaining available width

            dataSet.push(obj);
        });

        return dataSet;
    },

    /**
     * Builds a DataTable for the features
     */
    buildTable() {
        this.gridContainer = $(`#${this.gridId}`);
        const gridTableId = `${this.gridId}-table`;
        const gridFilterId = 'grid-filter-container';

        const gridTable = $(`<table class="display" id="${gridTableId}" width="100%"></table>`);
        gridTable.prepend(this.tableHeader);
        this.gridContainer.html(gridTable);

        // For MSIE browsers, the show/hide columns button uses the onmouseover activation method
        // This is done to prevent IE from activating the button dropdown on return keypress.
        const activationAction = navigator.appVersion.indexOf('MSIE') >= 0 ? 'mouseover' : 'click';

        // Change the error mode to thorw js errors instead of alerts.
        $.fn.dataTableExt.sErrMode = 'throw';

        this.grid = $(`#${gridTableId}`).dataTable({
            data: this.dataSet,
            columns: this.columns,
            columnDefs: [
                {
                    targets: [0],
                    visible: false,
                    searchable: false
                },
                {
                    targets: [-1],
                    bSortable: false,
                    searchable: false
                }
            ],
            fnCreatedRow: (nRow, aData, iDataIndex) => {
                $(nRow).attr('id', `${this.gridId}-${aData.urn}`);
            },
            sDom: `RC<".gridActionsLeft"<"#${gridFilterId}.left"f>><"clear">tS`,
            colVis: {
                buttonText: '',
                activate: activationAction,
                sAlign: 'right',
                exclude: [0, this.columns.length - 1]
            },
            sScrollY: this.calcDataTableHeight(),
            scrollCollapse: true,
            sScrollX: true,
            autoWidth: false,
            // We only include here the messages that are going to be shown based on the other
            // configuration options. For the full list see
            // http://datatables.net/manual/i18n
            language: {
                processing: this.msg('processing'),
                search: this.msg('filter')
            },
            order: [] // to remove the default ordering by the URN field
        });
        $(`#${gridTableId}`)
            .DataTable()
            .colResize.init();
        $(`#${gridFilterId} > div input`).addClass('text');
        this.gridContainer.find('.ColVis > button').attr('title', this.msg('show_hide_columns'));

        $(`#${gridTableId}`).on('search.dt', this._handleFilteredTable.bind(this));

        // To make sure that the grid-header is always aligned with the rest of the table
        this.gridContainer
            .find('.dataTables_scrollHeadInner')
            .width($(`#${gridTableId}`).width() - 17);
        this.gridContainer.find('.dataTables_scrollHeadInner').on('mousemove', () => {
            $(this).width($(`#${gridTableId}`).width());
        });

        if (L.Browser.android) {
            // Android native browsers don't recognize table-layout:fixed, hence we need to replace it with auto.
            $('table.dataTable').css('table-layout', 'auto !important');
        }
    },

    // method is called when fillter is applied to the table values.
    _handleFilteredTable() {
        if (this.grid) {
            const filteredList = this.grid
                .api()
                .rows({ filter: 'applied' })
                .data(); //list of filtered rows
            let listOfFilteredUrns = _.pluck(filteredList, 'urn');
            if (
                $(`#${this.gridId}-table_filter`)
                    .find('input')
                    .val() === ''
            )
                listOfFilteredUrns = undefined;
            if (this.options.onFilterChange) this.options.onFilterChange(listOfFilteredUrns);
        }
        const dataLength = this.grid.fnGetData().length,
            currentDataLength = this.grid.fnSettings().fnRecordsDisplay();

        if (dataLength > currentDataLength) {
            $(`#${this.gridId}-result-report`).html(
                this.msg('result_report_outof', { count: currentDataLength, outof: dataLength })
            );
        } else {
            $(`#${this.gridId}-result-report`).html(
                this.msg('result_report', { count: dataLength })
            );
        }
    },

    _scrollToFeature(feature) {
        if (feature) {
            $(this.grid.find('tr.grid-row-selected')).removeClass('grid-row-selected');
            const filteredRows = this.grid.api().rows({ filter: 'applied' }); // available rows after appllying the filter
            const featureList = filteredRows.data(); //list of filtered rows
            const urnList = _.pluck(featureList, 'urn');
            if (urnList.length !== 0) {
                const rowNumber = _.indexOf(urnList, feature.getUrn()); //  number of row to scroll to
                const featureId = feature.getUrn();
                const trDom = $(this.grid).find(
                    `#${$.escapeSelector(`${this.gridId}-${featureId}`)}`
                )[0];
                if (!this._isElementInViewport(trDom)) {
                    this._fnSettings = this._fnSettings || this.grid.fnSettings(); // we always use the same object for a table instance
                    this._fnSettings.oScroller.fnScrollToRow(rowNumber);
                }
                $(filteredRows.nodes()[rowNumber]).addClass('grid-row-selected');
            }
        }
    },
    // We don't want to scroll if the row is visible
    _isElementInViewport(trDom) {
        if (!trDom) return false;
        const tableDom = $($(this.grid).parent());
        const tableTop = tableDom.offset().top;
        const scollBodyBottom = tableTop + tableDom.height();
        const elemTop = $(trDom).offset().top;
        const elemBottom = elemTop + $(trDom).height();

        return elemTop > tableTop && elemBottom < scollBodyBottom;
    },

    calcDataTableHeight() {
        return (
            $(`#${this.gridId}`)
                .parent()
                .height() - 61
        ); // TO-DO: Calculate the height using the remaining space in the container
    },

    clear() {
        $(`#${this.gridId}`).empty();
    }
});

export default myw.DataTable;
